import { useEffect, useState, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { checkAuth, initializeFromStorage } from "./store/slices/authSlice";
import Layout from "./components/Layout";
import GuardRoute from "./components/GuardRoute";
import AutoLogout from "./components/AutoLogout";
import HelmetTitle from "./components/HelmetTitle";
import { Shield } from "lucide-react";
import "./styles/index.css";

// Lazy load public components
const Categories = lazy(() => import("./pages/Categories"));
const Category = lazy(() => import("./pages/Categories/Category"));
const Products = lazy(() => import("./pages/Products"));
const Product = lazy(() => import("./pages/Products/Product"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogTag = lazy(() => import("./pages/Blog/Tag"));
const BlogPost = lazy(() => import("./pages/Blog/Post"));
const BlogAuthor = lazy(() => import("./pages/Blog/Author"));
const BlogCategory = lazy(() => import("./pages/Blog/Category"));
const Home = lazy(() => import("./pages/Home"));
const Cart = lazy(() => import("./pages/Cart"));
const Sales = lazy(() => import("./pages/Sales"));
const Wishlist = lazy(() => import("./pages/Wishlish"));
const Contact = lazy(() => import("./pages/Contact"));
const Request = lazy(() => import("./pages/Request"));
const Order = lazy(() => import("./pages/Order"));
const Faqs = lazy(() => import("./pages/Faqs"));
const Terms = lazy(() => import("./pages/Terms"));
const About = lazy(() => import("./pages/About"));
const Error = lazy(() => import("./pages/Error"));
const Returns = lazy(() => import("./pages/Returns"));
const Privacy = lazy(() => import("./pages/Privacy"));

// Lazy load admin components
const AdminLogin = lazy(() => import("./pages/Admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/Admin/AdminLayout"));
const AdminsManagement = lazy(
  () => import("./pages/Admin/components/AdminsManagement"),
);
const OrdersManagement = lazy(
  () => import("./pages/Admin/components/OrdersManagement"),
);
const DashboardOverview = lazy(
  () => import("./pages/Admin/components/DashboardOverview"),
);
const SettingsManagement = lazy(
  () => import("./pages/Admin/components/SettingsManagement"),
);
const ProductsManagement = lazy(
  () => import("./pages/Admin/components/ProductsManagement"),
);
const CategoriesManagement = lazy(
  () => import("./pages/Admin/components/CategoriesManagement"),
);
const NotificationsManagement = lazy(
  () => import("./pages/Admin/components/NotificationsManagement"),
);
const ContactsManagement = lazy(
  () => import("./pages/Admin/components/ContactsManagement"),
);
const RequestsManagement = lazy(
  () => import("./pages/Admin/components/RequestsManagement"),
);
const BlogManagement = lazy(
  () => import("./pages/Admin/components/BlogManagement"),
);

// Loading component for Suspense
const PageLoader = () => (
  <div
    className="fixed inset-0 flex items-center justify-center transition-colors duration-300 z-50"
    style={{ background: "var(--bg-primary)" }}
  >
    <div className="relative">
      <div className="w-20 h-20 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-primary-500 animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-500 to-primary-700 flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  </div>
);

// Wrapper component for routes that need Helmet
const RouteWrapper = ({ children, title, dynamicTitle }) => {
  const finalTitle = dynamicTitle || title;

  return (
    <>
      <HelmetTitle title={finalTitle} />
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </>
  );
};

// Helper function to check if admin cookie exists
const hasAdminCookie = () => {
  if (typeof document === "undefined") return false;
  return (
    document.cookie.includes("admin_access_token=") ||
    document.cookie.includes("admin_refresh_token=")
  );
};

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { theme, effectiveTheme } = useSelector((state) => state.theme);
  const { isAuthenticated, error } = useSelector((state) => state.auth);
  const [initialized, setInitialized] = useState(false);

  // Update document title on route change
  useEffect(() => {
    // This ensures title updates even if Helmet fails
    const path = location.pathname;
    let pageTitle = "متجر بروتاكتا للسلامة";

    if (path === "/") pageTitle = "الرئيسية";
    else if (path.includes("/products")) pageTitle = "المنتجات";
    else if (path.includes("/product")) pageTitle = "تفاصيل المنتج";
    else if (path.includes("/categories")) pageTitle = "الفئات";
    else if (path.includes("/category")) pageTitle = "تفاصيل الفئة";
    else if (path.includes("/blog")) pageTitle = "المدونة";
    else if (path.includes("/cart")) pageTitle = "سلة التسوق";
    else if (path.includes("/wishlist")) pageTitle = "المفضلة";
    else if (path.includes("/about")) pageTitle = "من نحن";
    else if (path.includes("/contact")) pageTitle = "اتصل بنا";
    else if (path.includes("/request")) pageTitle = "طلب بيع";
    else if (path.includes("/sales")) pageTitle = "العروض";
    else if (path.includes("/login")) pageTitle = "تسجيل الدخول";
    else if (path.includes("/admin")) pageTitle = "لوحة التحكم";

    document.title = `${pageTitle} | متجر بروتاكتا للسلامة`;
  }, [location]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", effectiveTheme);
    root.style.colorScheme = effectiveTheme;
  }, [effectiveTheme, theme]);

  useEffect(() => {
    const initAuth = async () => {
      dispatch(initializeFromStorage());
      const cookiesExist = hasAdminCookie();
      const userStr = localStorage.getItem("admin_user");
      const user = userStr ? JSON.parse(userStr) : null;

      if (user && cookiesExist) {
        try {
          await dispatch(checkAuth()).unwrap();
        } catch (err) {
          localStorage.removeItem("admin_user");
        }
      }

      setInitialized(true);
    };

    initAuth();
  }, [dispatch]);

  if (!initialized) {
    return (
      <>
        <HelmetTitle title="جاري التحميل..." />
        <div
          className="flex items-center justify-center min-h-screen transition-colors duration-300"
          style={{ background: "var(--bg-primary)" }}
        >
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center bg-gradient-to-r from-primary-500 to-primary-700">
              <Shield className="w-8 h-8 text-white animate-pulse" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              جاري تحميل التطبيق...
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AutoLogout />
      <div
        className="min-h-screen transition-colors duration-300"
        style={{ background: "var(--bg-primary)" }}
      >
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* Public routes */}
            <Route
              index
              element={
                <RouteWrapper title="الرئيسية">
                  <Home />
                </RouteWrapper>
              }
            />
            <Route
              path="about"
              element={
                <RouteWrapper title="من نحن">
                  <About />
                </RouteWrapper>
              }
            />
            <Route
              path="cart"
              element={
                <RouteWrapper title="سلة التسوق">
                  <Cart />
                </RouteWrapper>
              }
            />
            <Route
              path="wishlist"
              element={
                <RouteWrapper title="المفضلة">
                  <Wishlist />
                </RouteWrapper>
              }
            />
            <Route
              path="products"
              element={
                <RouteWrapper title="المنتجات">
                  <Products />
                </RouteWrapper>
              }
            />
            <Route
              path="products/:slug"
              element={
                <RouteWrapper title="تفاصيل المنتج">
                  <Product />
                </RouteWrapper>
              }
            />
            <Route
              path="categories"
              element={
                <RouteWrapper title="الفئات">
                  <Categories />
                </RouteWrapper>
              }
            />
            <Route
              path="categories/:slug"
              element={
                <RouteWrapper title="تفاصيل الفئة">
                  <Category />
                </RouteWrapper>
              }
            />
            <Route
              path="blog"
              element={
                <RouteWrapper title="مدونة بروتاكتا">
                  <Blog />
                </RouteWrapper>
              }
            />
            <Route
              path="blog/tag/:title"
              element={
                <RouteWrapper title="الوسوم">
                  <BlogTag />
                </RouteWrapper>
              }
            />
            <Route
              path="blog/post/:slug"
              element={
                <RouteWrapper title="مقال">
                  <BlogPost />
                </RouteWrapper>
              }
            />
            <Route
              path="blog/author/:id"
              element={
                <RouteWrapper title="مقالات الكاتب">
                  <BlogAuthor />
                </RouteWrapper>
              }
            />
            <Route
              path="blog/category/:slug"
              element={
                <RouteWrapper title="التصنيف">
                  <BlogCategory />
                </RouteWrapper>
              }
            />
            <Route
              path="orders/:id"
              element={
                <RouteWrapper title="تفاصيل الطلب">
                  <Order />
                </RouteWrapper>
              }
            />
            <Route
              path="contact"
              element={
                <RouteWrapper title="اتصل بنا">
                  <Contact />
                </RouteWrapper>
              }
            />
            <Route
              path="request"
              element={
                <RouteWrapper title="طلب بيع">
                  <Request />
                </RouteWrapper>
              }
            />
            <Route
              path="sales"
              element={
                <RouteWrapper title="العروض">
                  <Sales />
                </RouteWrapper>
              }
            />
            <Route
              path="privacy"
              element={
                <RouteWrapper title="سياسة الخصوصية">
                  <Privacy />
                </RouteWrapper>
              }
            />
            <Route
              path="returns"
              element={
                <RouteWrapper title="سياسة الإرجاع">
                  <Returns />
                </RouteWrapper>
              }
            />
            <Route
              path="terms"
              element={
                <RouteWrapper title="الشروط والأحكام">
                  <Terms />
                </RouteWrapper>
              }
            />
            <Route
              path="faqs"
              element={
                <RouteWrapper title="الأسئلة الشائعة">
                  <Faqs />
                </RouteWrapper>
              }
            />
            <Route
              path="*"
              element={
                <RouteWrapper title="الصفحة غير موجودة">
                  <Error />
                </RouteWrapper>
              }
            />
          </Route>

          {/* Admin Login */}
          <Route
            path="/login"
            element={
              <Suspense fallback={<PageLoader />}>
                {(() => {
                  const cookiesExist = hasAdminCookie();
                  const userStr = localStorage.getItem("admin_user");
                  const user = userStr ? JSON.parse(userStr) : null;

                  if (cookiesExist && user && isAuthenticated && !error) {
                    return <Navigate to="/admin" replace />;
                  }
                  return (
                    <RouteWrapper title="تسجيل الدخول">
                      <AdminLogin />
                    </RouteWrapper>
                  );
                })()}
              </Suspense>
            }
          />

          {/* Protected Admin Routes */}
          <Route
            path="/admin"
            element={
              <GuardRoute>
                <AdminLayout />
              </GuardRoute>
            }
          >
            <Route
              index
              element={
                <RouteWrapper title="لوحة التحكم">
                  <DashboardOverview />
                </RouteWrapper>
              }
            />
            <Route
              path="statistics"
              element={<Navigate to="/admin" replace />}
            />
            <Route
              path="products"
              element={
                <RouteWrapper title="إدارة المنتجات">
                  <ProductsManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="categories"
              element={
                <RouteWrapper title="إدارة الفئات">
                  <CategoriesManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="contacts"
              element={
                <RouteWrapper title="إدارة جهات الاتصال">
                  <ContactsManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="orders"
              element={
                <RouteWrapper title="إدارة الطلبات">
                  <OrdersManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="blog"
              element={
                <RouteWrapper title="إدارة المدونة">
                  <BlogManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="requests"
              element={
                <RouteWrapper title="إدارة الطلبات">
                  <RequestsManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="admins"
              element={
                <RouteWrapper title="إدارة المشرفين">
                  <AdminsManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="settings"
              element={
                <RouteWrapper title="الإعدادات">
                  <SettingsManagement />
                </RouteWrapper>
              }
            />
            <Route
              path="notifications"
              element={
                <RouteWrapper title="الإشعارات">
                  <NotificationsManagement />
                </RouteWrapper>
              }
            />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

export default App;
